import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { createAgenticCartOrder } from "@/lib/api/plans";
import { createCheckoutEventContext } from "@/lib/checkout-event-context";
import { AGENTIC_CART_SELECTED_OFFER_REASON } from "./AgenticCartCheckout.attribution";
import type { AgenticCartCallbacks, ContactForm, ShippingForm } from "./AgenticCartCheckout.types";
import { openRazorpay } from "./AgenticCartCheckout.razorpay";
import { recordNativeCheckoutStarted } from "./AgenticCartCheckout.telemetry";

type StartAgenticCartCheckoutInput = AgenticCartCallbacks & {
  bundle: AgenticCartBundleSummaryResponse;
  contact: ContactForm;
  shipping: ShippingForm;
  sourcePath: string;
  intent: string;
  customerProblem: string;
  agentTraceId?: string;
  journeyActionId?: string;
  concernId?: string;
  readingId?: string;
  language?: "en" | "hi";
  /** Pre-checked WhatsApp order/delivery opt-in — threads into
   *  recovery_phone_consent on the abandon payload. */
  whatsappConsent?: boolean;
};

export async function startAgenticCartCheckout({
  bundle,
  contact,
  shipping,
  sourcePath,
  intent,
  customerProblem,
  agentTraceId,
  journeyActionId,
  concernId,
  readingId,
  language = "en",
  whatsappConsent = true,
  onSuccess,
  onStatus,
  onError,
  onRecovery,
}: StartAgenticCartCheckoutInput) {
  const validationError = validateCheckout(bundle, contact, shipping);
  if (validationError) {
    onError(validationError);
    onStatus("ready");
    return;
  }

  onError("");
  onStatus("paying");
  const checkoutEvent = createCheckoutEventContext();
  const reportInputs = agenticReportInputs(contact, readingId, language);
  try {
    const order = await createNativeCartOrder({
      bundle,
      contact,
      shipping,
      sourcePath,
      intent,
      customerProblem,
      agentTraceId,
      journeyActionId,
      concernId,
      readingId,
      reportInputs,
      event_id: checkoutEvent.event_id,
    });
    recordNativeCheckoutStarted({
      bundle,
      order,
      sourcePath,
      intent,
      customerProblem,
      agentTraceId,
      journeyActionId,
      concernId,
    });
    await openRazorpay({
      bundle,
      order,
      contact,
      sourcePath,
      intent,
      customerProblem,
      agentTraceId,
      journeyActionId,
      concernId,
      reportInputs,
      language,
      whatsappConsent,
      eventId: checkoutEvent.eventId,
      onSuccess,
      onStatus,
      onError,
      onRecovery,
    });
  } catch (err) {
    onError(err instanceof Error ? err.message : "Could not start checkout. Try again.");
    onStatus("ready");
  }
}

function createNativeCartOrder({
  bundle,
  contact,
  shipping,
  sourcePath,
  intent,
  customerProblem,
  agentTraceId,
  journeyActionId,
  concernId,
  readingId,
  reportInputs,
  event_id,
}: {
  bundle: AgenticCartBundleSummaryResponse;
  contact: ContactForm;
  shipping: ShippingForm;
  sourcePath: string;
  intent: string;
  customerProblem: string;
  agentTraceId?: string;
  journeyActionId?: string;
  concernId?: string;
  readingId?: string;
  reportInputs: Record<string, unknown>;
  event_id: string;
}) {
  return createAgenticCartOrder({
    bundle_id: bundle.bundle_id,
    amount_inr: bundle.price_inr,
    customer_email: contact.email.trim(),
    customer_phone: contact.phone.trim(),
    reading_id: readingId,
    report_inputs: reportInputs,
    shipping_address: bundle.requires_shipping ? normalizedShipping(contact, shipping) : undefined,
    source_path: sourcePath,
    intent,
    customer_problem: customerProblem,
    journey_action_id: journeyActionId,
    concern_id: concernId,
    agent_trace_id: agentTraceId,
    selected_offer_reason: AGENTIC_CART_SELECTED_OFFER_REASON,
    ga_client_id: getGoogleAnalyticsClientId(),
    idempotency_key: event_id,
    event_id,
  });
}

export function agenticReportInputs(
  contact: ContactForm,
  readingId?: string,
  language: "en" | "hi" = "en"
): Record<string, unknown> {
  return {
    name: contact.name.trim(),
    birth_date: contact.birthDate,
    birth_time: contact.birthTime,
    birth_place: contact.birthPlace.trim(),
    language,
    ...(readingId ? { reading_id: readingId } : {}),
  };
}

function validateCheckout(
  bundle: AgenticCartBundleSummaryResponse,
  contact: ContactForm,
  shipping: ShippingForm
): string {
  if (!contact.name.trim()) return "Name is required for the report.";
  if (!contact.birthDate || !contact.birthPlace.trim()) {
    return "Birth date and birth place are required for the report.";
  }
  if (!contact.email.trim() || !contact.phone.trim()) {
    return "Email and WhatsApp are required for delivery and support.";
  }
  if (contact.email.trim() && !contact.email.includes("@")) return "Enter a valid email.";
  if (!bundle.requires_shipping) return "";
  if (isShippingIncomplete(contact, shipping)) {
    return "Complete the delivery address for the physical remedy items.";
  }
  return "";
}

function isShippingIncomplete(contact: ContactForm, shipping: ShippingForm): boolean {
  return (
    !(shipping.first_name.trim() || contact.name.trim()) ||
    !shipping.address1.trim() ||
    !shipping.city.trim() ||
    !shipping.zip.trim() ||
    !normalizedPhone(contact, shipping)
  );
}

function normalizedShipping(contact: ContactForm, shipping: ShippingForm): ShippingForm {
  return {
    ...shipping,
    first_name: shipping.first_name.trim() || contact.name.trim().split(/\s+/)[0] || "",
    country_code: shipping.country_code || "IN",
    phone: normalizedPhone(contact, shipping),
  };
}

function normalizedPhone(contact: ContactForm, shipping: ShippingForm): string {
  return shipping.phone.trim() || contact.phone.trim();
}
